import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import SiteWrapper from "components/Layouts/SiteWrapper";
import DetailsPanel from "components/Elements/DetailsPanel";
import DeleteDialog from "components/Elements/DeleteDialog";
import TabTitle from "components/Elements/TabTitle";

import { fetchSiteDetail } from "../../../../redux/siteDetail/actions";
import { showError } from "../../../../redux/common/actions";
import { BASE_API_PATH, AccessTypes } from "helpers/constants";
import { getLocalStorageData } from "helpers/utils";
import { getSiteNavigationFromStorage } from "helpers/siteNavigation";

import useAssetData from "./hooks/useAssetData";
import useSAPMode from "./hooks/useSAPMode";
import AssetRegisterTable from "./AssetRegisterTable";
import AssetDialog from "./dialogs/AssetDialog";
import ImportDialog from "./dialogs/ImportDialog";
import SearchBox from "./components/SearchBox";

const useStyles = makeStyles()((theme) => ({
	container: {
		padding: "20px 0",
	},
	detailsContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		marginBottom: "20px",
		"@media (max-width: 768px)": {
			flexDirection: "column",
			gap: "15px",
		},
	},
}));

const AssetRegister = ({ fetchCrumbs, getError, siteDetails }) => {
	const { classes } = useStyles();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id, clientId } = useParams();

	const { position, siteAppID, customCaptions, application } = getLocalStorageData("me");
	
	const navigation = getSiteNavigationFromStorage(getLocalStorageData);

	const showAssetReferences = application?.showAssetReferences || false;
	const showAddImport = position?.assetAccess === AccessTypes.Full || !siteAppID;

    const [searchTerm, setSearchTerm] = useState("");
    const [currentTableSort, setCurrentTableSort] = useState(["assetNo", "asc"]);
    const [page, setPage] = useState({ pageNo: 1, perPage: 25 });
    const [modal, setModal] = useState({ 
        delete: false, 
        edit: false, 
        add: false, 
        import: false 
    });
    const [selectedRow, setSelectedRow] = useState(null);
    const [editData, setEditData] = useState({});

    const assetData = useAssetData(id, customCaptions);
    const sapMode = useSAPMode(showAssetReferences, customCaptions);

    const assetDataRef = useRef();
    assetDataRef.current = assetData;
    
    const searchTermRef = useRef(searchTerm);
    searchTermRef.current = searchTerm;

    const handleSearch = useCallback((searchText) => {
        setSearchTerm(searchText);
    }, []);

    const handleSort = useCallback(async (sortField, sortOrder) => {
        setCurrentTableSort([sortField, sortOrder]);
        await assetDataRef.current.fetchAssets(1, sortField, sortOrder, searchTerm, showAssetReferences);
        setPage({ pageNo: 1, perPage: 25 });
    }, [searchTerm, showAssetReferences]);

	const handlePageChange = useCallback(async (pageNo, prevData, sortOptions) => {
		const result = await assetDataRef.current.loadMoreAssets(
			pageNo,
			prevData,
			sortOptions,
			searchTermRef.current,
			showAssetReferences
		);

		if (result.success) {
			setPage({ pageNo, perPage: 25 });
		}

		return result;
	}, [showAssetReferences]);

	const handleEdit = useCallback((rowId) => {
		const row = assetData.data.find(item => item.id === rowId);
		if (row) {
			setSelectedRow(row);
			setEditData(sapMode.getEditableData(row));
			setModal(prev => ({ ...prev, edit: true }));
		}
	}, [assetData.data, sapMode]);

	const handleDelete = useCallback((rowId) => {
		const row = assetData.data.find(item => item.id === rowId);
		if (row) {
			setSelectedRow(row);
			setModal(prev => ({ ...prev, delete: true }));
		}
	}, [assetData.data]);

    const handleAdd = useCallback(() => {
        setModal(prev => ({ ...prev, add: true }));
    }, []);

	const handleImport = useCallback(() => {
		setModal(prev => ({ ...prev, import: true }));
	}, []);

	const closeModal = useCallback((modalType) => {
		setModal(prev => ({ ...prev, [modalType]: false }));
		setSelectedRow(null);
		setEditData({});
	}, []);

	const refreshData = useCallback(async () => {
		await assetData.fetchAssets(
			1,
			currentTableSort[0],
			currentTableSort[1],
			searchTerm,
			showAssetReferences
		);
		setPage({ pageNo: 1, perPage: 25 });
	}, [assetData, currentTableSort, searchTerm, showAssetReferences]);

	const tableData = useMemo(() => {
		return assetData.data.map(row => ({
			...row,
			displayData: sapMode.getRowDisplayData(row)
		}));
	}, [assetData.data, sapMode]);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            assetDataRef.current.fetchAssets(1, currentTableSort[0], currentTableSort[1], "", showAssetReferences);
        } else {
            assetDataRef.current.searchAssets(searchTerm, currentTableSort[0], currentTableSort[1], showAssetReferences);
        }
        setPage({ pageNo: 1, perPage: 25 });
    }, [searchTerm, showAssetReferences, currentTableSort]);

	useEffect(() => {
		const initializeData = async () => {
			try {
				await fetchCrumbs(id, clientId);
				await assetData.fetchAssets(1, "assetNo", "asc", "", showAssetReferences);
			} catch (err) {
				dispatch(showError(`Failed to load ${customCaptions?.assetPlural || "assets"}.`));
			}
		};

		initializeData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, clientId, showAssetReferences]);

	if (position?.assetAccess === AccessTypes.None) {
		navigate(-1);
		return null;
	}

	const handleEditSuccess = async () => {
		await refreshData();
	};

	const handleAddSuccess = async () => {
		await refreshData();
	};

	const handleImportSuccess = async () => {
		await refreshData();
	};

	const handleDeleteSuccess = async () => {
		await refreshData();
		closeModal('delete');
	};

	const pageTitle = `${siteDetails?.name || "Site"} ${customCaptions?.assetPlural || "Assets"}`;
	const entityName = sapMode.isSAPMode 
		? (customCaptions?.assetReference || "Asset Reference") 
		: (customCaptions?.asset || "Asset");
	const deleteEndpoint = sapMode.isSAPMode 
		? `${BASE_API_PATH}SiteAssetReferences`
		: `${BASE_API_PATH}SiteAssets`;

	const deleteId = sapMode.isSAPMode 
		? selectedRow?.referenceId 
		: selectedRow?.assetId;

	return (
		<>
			<TabTitle title={pageTitle} />

			<DeleteDialog
				entityName={entityName}
				open={modal.delete}
				deleteID={deleteId}
				deleteEndpoint={deleteEndpoint}
				handleRemoveData={handleDeleteSuccess}
				closeHandler={() => closeModal('delete')}
			/>

            <AssetDialog
                open={modal.edit}
                closeHandler={() => closeModal('edit')}
                onSuccess={handleEditSuccess}
                getError={getError}
                isEdit
                isSAPMode={sapMode.isSAPMode}
                siteId={id}
                data={editData}
            />

            <AssetDialog
                open={modal.add}
                closeHandler={() => closeModal('add')}
                onSuccess={handleAddSuccess}
                getError={getError}
                isEdit={false}
                isSAPMode={sapMode.isSAPMode}
                siteId={id}
                data={{}}
            />

			<ImportDialog
				open={modal.import}
				closeHandler={() => closeModal('import')}
				siteId={id}
				onImportSuccess={handleImportSuccess}
				getError={getError}
				showAssetReferences={showAssetReferences}
			/>

			<SiteWrapper
				current={customCaptions?.assetPlural || "Assets"}
				navigation={navigation}
				onNavClick={(urlToGo) =>
					navigate(`/app/clients/${clientId}/sites/${id}${urlToGo}`)
				}
				status=""
				lastSaved=""
                showAdd={showAddImport}
				showImport={showAddImport}
				onClickImport={handleImport}
				onClickAdd={handleAdd}
			>
				<div className={classes.container}>
					<div className={classes.detailsContainer}>
                        <DetailsPanel
                            header={customCaptions?.assetPlural || "Assets"}
                            dataCount={assetData.count}
                            description={
                                sapMode.isSAPMode
                                    ? `View and manage all ${customCaptions?.assetPlural?.toLowerCase() || "assets"} and their functional locations (FLOC) in a single register`
                                    : `Create and manage ${customCaptions?.assetPlural?.toLowerCase() || "assets"} that can be assigned`
                            }
                        />

						<SearchBox
							value={searchTerm}
							onSearch={handleSearch}
							placeholder={`Search ${customCaptions?.assetPlural || "Assets"}`}
							delay={500}
						/>
					</div>

                    <AssetRegisterTable
                        data={tableData}
                        tableConfig={sapMode.tableConfig}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        page={page.pageNo}
                        onPageChange={handlePageChange}
                        count={assetData.count}
                        isLoading={assetData.isLoading}
                        searchText={searchTerm}
                        isReadOnly={!showAddImport}
                        currentTableSort={currentTableSort}
                        handleSort={handleSort}
                        isSAPMode={sapMode.isSAPMode}
                        customCaptions={customCaptions}
                    />
				</div>
			</SiteWrapper>
		</>
	);
};

const mapStateToProps = ({ siteDetailData: { siteDetails } }) => ({
	siteDetails,
});

const mapDispatchToProps = (dispatch) => ({
	fetchCrumbs: (id, clientId) => dispatch(fetchSiteDetail(id, clientId)),
	getError: (msg) => dispatch(showError(msg)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssetRegister);
